(() => {
  const counts = [1, 2, 5];
  const normalize = value => String(value ?? '').toLowerCase().trim();
  const positions = product => ({
    color: product.options.findIndex(name => /^(colou?r|couleur)$/.test(normalize(name))),
    size: product.options.findIndex(name => /^(size|eu size|taille|pointure)$/.test(normalize(name)))
  });
  const blackColor = product => {
    const position = positions(product).color;
    if (position < 0) return null;
    return product.variants.flatMap(variant => variant.options[position]).find(value => normalize(value) === 'black') || null;
  };
  const resolve = (product, options) => product.variants.find(variant => variant.options.length === options.length && variant.options.every((value, index) => value === options[index]));
  const stock = variant => variant.inventoryManaged ? Math.max(0, Number(variant.inventoryQuantity) || 0) : Infinity;
  const itemsFor = (product, pairs, count, existing = []) => {
    if (!product || product.handle !== 'crewford-work-socks' || !counts.includes(count) || pairs.length !== count) return { items: [], error: 'configuration', invalid: [] };
    const colorPosition = positions(product).color;
    if (colorPosition < 0 || pairs.some(options => normalize(options[colorPosition]) !== 'black')) return { items: [], error: 'configuration', invalid: [] };
    const selected = pairs.map(options => resolve(product, options));
    const invalid = selected.flatMap((variant, index) => !variant?.available || stock(variant) < 1 ? [index] : []);
    if (invalid.length) return { items: [], error: 'unavailable', invalid };
    const quantities = new Map();
    selected.forEach(variant => quantities.set(variant.id, (quantities.get(variant.id) || 0) + 1));
    const over = new Set();
    quantities.forEach((quantity, id) => {
      const variant = selected.find(item => item.id === id);
      const inCart = existing.reduce((sum, item) => sum + (String(item.variant_id ?? item.id) === String(id) ? Number(item.quantity) || 0 : 0), 0);
      if (quantity + inCart > stock(variant)) over.add(id);
    });
    if (over.size) return { items: [], error: 'stock', invalid: selected.flatMap((variant, index) => over.has(variant.id) ? [index] : []) };
    return { items: [...quantities].map(([id, quantity]) => ({ id, quantity })), error: null, invalid: [] };
  };
  const totalFor = (product, count, currency = 'USD') => {
    const base = Number(product?.variants[0]?.price) || 0;
    const unit = count >= 5 ? 1298 : count >= 2 ? 2495 : 2690;
    return currency === 'USD' ? unit * count : Math.round(base * unit / 2690) * count;
  };
  const model = Object.freeze({ counts, positions, blackColor, resolve, stock, itemsFor, totalFor });
  window.CrewfordOrderModel = model;
  if (customElements.get('crewford-order')) return;
  class CrewfordOrder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abortController = new AbortController();
      this.form = this.querySelector('form');
      try {
        this.config = JSON.parse(this.querySelector('[data-cfo-config]').textContent);
        this.product = this.config.product;
        this.money = new Intl.NumberFormat(this.config.locale, { style: 'currency', currency: this.config.currency });
        this.root = this.config.root.endsWith('/') ? this.config.root : `${this.config.root}/`;
        this.cartItems = this.config.cartItems || [];
        this.slide = 0;
        this.setupGallery();
        if (!this.form || !this.product?.variants?.length || this.product.handle !== 'crewford-work-socks') return;
        this.positions = positions(this.product);
        this.black = blackColor(this.product);
        this.validConfig = this.product.options.length === 2 && this.positions.color >= 0 && this.positions.size >= 0 && Boolean(this.black) && (this.config.currency !== 'USD' || this.product.variants.every(variant => variant.price === 2690));
        this.pairs = [];
        const params = new URLSearchParams(location.search);
        const requested = Number(params.get('pack'));
        this.pack = counts.includes(requested) ? requested : 5;
        const requestedVariant = this.product.variants.find(variant => String(variant.id) === params.get('variant') && normalize(variant.options[this.positions.color]) === 'black');
        const selectedSize = (requestedVariant || this.product.variants.find(variant => variant.id === this.product.selectedVariantId))?.options[this.positions.size];
        this.initialVariant = requestedVariant || this.product.variants.find(variant => normalize(variant.options[this.positions.color]) === 'black' && variant.options[this.positions.size] === selectedSize) || this.product.variants.find(variant => normalize(variant.options[this.positions.color]) === 'black');
        this.size = this.initialVariant?.options[this.positions.size] || '';
        this.querySelectorAll('[data-cfo-fallback]').forEach(input => { input.disabled = true; });
        this.querySelector('[data-cfo-interactive]').hidden = false;
        this.classList.add('cfo--ready');
        const signal = this.abortController.signal;
        this.addEventListener('change', event => {
          if (event.target.matches('[data-cfo-pack]')) this.setPack(Number(event.target.value));
          if (event.target.matches('[data-cfo-pair-option]')) {
            this.size = event.target.value;
            this.syncPairs();
            this.clearMessage();
            this.paint();
          }
        }, { signal });
        this.form.addEventListener('submit', event => this.addToCart(event), { signal });
        document.documentElement.addEventListener('cart:change', event => {
          if (Array.isArray(event.detail?.cart?.items)) {
            this.cartItems = event.detail.cart.items;
            if (!this.busy && !this.uncertain) this.clearMessage();
            this.paint();
          }
        }, { signal });
        window.addEventListener('hashchange', () => this.openSizeGuide(), { signal });
        this.setPack(this.pack);
        this.openSizeGuide();
      } catch (error) {
        this.showError(this.config?.labels?.configuration || 'This product is not ready to order yet.');
        const button = this.querySelector('[data-cfo-submit]');
        if (button) button.disabled = true;
      }
    }
    disconnectedCallback() {
      this.abortController?.abort();
      this.initialized = false;
    }
    format(cents) { return this.money.format(cents / 100); }
    openSizeGuide() {
      if (location.hash !== '#crewford-size-guide') return;
      const guide = this.querySelector('#crewford-size-guide');
      if (!guide) return;
      guide.open = true;
      requestAnimationFrame(() => guide.scrollIntoView({ block: 'center' }));
    }
    setupGallery() {
      const signal = this.abortController.signal;
      this.addEventListener('click', event => {
        const thumb = event.target.closest('[data-cfo-thumb]');
        if (thumb) this.go(Number(thumb.dataset.cfoThumb));
        if (event.target.closest('[data-cfo-prev]')) this.go(this.slide - 1);
        if (event.target.closest('[data-cfo-next]')) this.go(this.slide + 1);
      }, { signal });
      const stage = this.querySelector('.cfo__stage');
      if (!stage) return;
      stage.addEventListener('keydown', event => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.go(this.slide + (event.key === 'ArrowRight' ? 1 : -1));
        }
      }, { signal });
      stage.addEventListener('pointerdown', event => { this.touchStart = event.clientX; }, { signal });
      stage.addEventListener('pointerup', event => {
        if (event.pointerType === 'touch' && Number.isFinite(this.touchStart) && Math.abs(event.clientX - this.touchStart) > 40) this.go(this.slide + (event.clientX < this.touchStart ? 1 : -1));
        this.touchStart = null;
      }, { signal });
      stage.addEventListener('pointercancel', () => { this.touchStart = null; }, { signal });
      this.go(0);
    }
    go(index) {
      const slides = this.querySelectorAll('.cfo__slide');
      this.slide = Math.max(0, Math.min(slides.length - 1, index));
      const track = this.querySelector('[data-cfo-track]');
      if (track) track.style.transform = `translateX(-${this.slide * 100}%)`;
      this.querySelectorAll('[data-cfo-thumb]').forEach((button, position) => {
        button.classList.toggle('is-selected', position === this.slide);
        button.setAttribute('aria-pressed', String(position === this.slide));
      });
      const previous = this.querySelector('[data-cfo-prev]');
      const next = this.querySelector('[data-cfo-next]');
      if (previous) { previous.hidden = slides.length < 2; previous.disabled = this.slide === 0; }
      if (next) { next.hidden = slides.length < 2; next.disabled = this.slide >= slides.length - 1; }
    }
    setPack(count) {
      if (!counts.includes(count) || this.busy) return;
      this.pack = count;
      this.syncPairs();
      this.renderPairs();
      this.clearMessage();
      this.paint();
    }
    syncPairs() {
      const options = [...(this.initialVariant?.options || [])];
      options[this.positions.color] = this.black;
      options[this.positions.size] = this.size;
      this.pairs = Array.from({ length: this.pack }, () => [...options]);
    }
    sizeHint(value) {
      const key = String(value).replace(/[^0-9]/g, '');
      const hints = { '3840': 'US 5.5–7 · UK 5–6', '4143': 'US 8–9.5 · UK 7–8.5', '4446': 'US 10–12 · UK 9–11' };
      return hints[key] || '';
    }
    sizeName(value) {
      const key = String(value).replace(/[^0-9]/g, '');
      return ({ '3840': 'M', '4143': 'L', '4446': 'XL' })[key] || value;
    }
    renderPairs() {
      const container = this.querySelector('[data-cfo-pairs]');
      container.replaceChildren();
      const row = document.createElement('div');
      row.className = 'cfo__size-picker';
      row.dataset.cfoPairRow = '0';
      const sizes = document.createElement('div');
      sizes.className = 'cfo__sizes';
      [...new Set(this.product.variants.filter(variant => normalize(variant.options[this.positions.color]) === 'black').map(variant => variant.options[this.positions.size]))].forEach((value, index) => {
        const label = document.createElement('label');
        label.className = 'cfo__size';
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = `${this.form.id}-size`;
        input.value = value;
        input.id = `${this.form.id}-size-${index}`;
        input.dataset.cfoPairOption = '';
        input.dataset.position = this.positions.size;
        input.checked = value === this.size;
        const name = document.createElement('b');
        name.textContent = this.sizeName(value);
        const range = document.createElement('small');
        range.textContent = `EU ${value}`;
        label.append(input, name, range);
        sizes.append(label);
      });
      row.append(sizes);
      const hint = document.createElement('small');
      hint.className = 'cfo__pair-hint';
      hint.dataset.cfoSizeHint = '';
      row.append(hint);
      container.append(row);
    }
    selection(existing = this.cartItems) { return this.validConfig ? itemsFor(this.product, this.pairs, this.pack, existing) : { items: [], error: 'configuration', invalid: [] }; }
    paint() {
      const selection = this.selection();
      const allUnavailable = !this.product.variants.some(variant => variant.available && stock(variant) > 0);
      this.querySelectorAll('[data-cfo-pack-card]').forEach(card => {
        const count = Number(card.dataset.cfoPackCard);
        const total = totalFor(this.product, count, this.config.currency);
        card.classList.toggle('is-selected', count === this.pack);
        card.querySelector('input').checked = count === this.pack;
        card.querySelector('input').disabled = Boolean(this.busy);
        card.querySelector('[data-cfo-total]').textContent = this.format(total);
        card.querySelector('[data-cfo-unit]').textContent = this.format(total / count);
      });
      this.querySelectorAll('[data-cfo-pair-row]').forEach(row => {
        const options = this.pairs[0];
        const invalid = !allUnavailable && selection.invalid.length > 0;
        row.classList.toggle('is-invalid', invalid);
        row.querySelector('[data-cfo-size-hint]').textContent = this.sizeHint(options[this.positions.size]);
        row.querySelectorAll('[data-cfo-pair-option]').forEach(input => {
          const position = Number(input.dataset.position);
          const possible = this.product.variants.some(variant => variant.available && stock(variant) > 0 && normalize(variant.options[this.positions.color]) === 'black' && variant.options[position] === input.value);
          input.disabled = Boolean(this.busy || (!allUnavailable && !possible));
          input.checked = input.value === this.size;
          input.setAttribute('aria-invalid', String(invalid));
          input.closest('.cfo__size')?.classList.toggle('is-selected', input.checked);
        });
      });
      const button = this.querySelector('[data-cfo-submit]');
      button.disabled = Boolean(selection.error || this.busy || this.uncertain);
      const label = this.querySelector('[data-cfo-submit-label]');
      label.textContent = this.busy ? this.config.labels.adding : allUnavailable ? this.config.labels.sold_out : `${this.config.labels.add} · ${this.format(totalFor(this.product, this.pack, this.config.currency))}`;
      if (allUnavailable && selection.error === 'unavailable' && !this.uncertain) this.querySelector('[data-cfo-error]').hidden = true;
      else if (selection.error && this.querySelector('[data-cfo-status]').hidden) this.showError(this.config.labels[selection.error]);
    }
    clearMessage() {
      const error = this.querySelector('[data-cfo-error]');
      if (error) error.hidden = true;
      const status = this.querySelector('[data-cfo-status]');
      if (status) status.hidden = true;
      const link = this.querySelector('[data-cfo-cart-link]');
      if (link && !this.uncertain) link.hidden = true;
    }
    showError(text) {
      const error = this.querySelector('[data-cfo-error]');
      if (error) { error.textContent = text; error.hidden = false; }
    }
    async addToCart(event) {
      event.preventDefault();
      if (this.busy || this.uncertain || CrewfordOrder.cartBusy) return;
      const initial = this.selection();
      if (initial.error) {
        const allUnavailable = !this.product.variants.some(variant => variant.available && stock(variant) > 0);
        if (allUnavailable && initial.error === 'unavailable') this.paint();
        else this.showError(this.config.labels[initial.error]);
        return;
      }
      this.busy = true;
      CrewfordOrder.cartBusy = true;
      this.clearMessage();
      this.form.setAttribute('aria-busy', 'true');
      this.paint();
      let added = false;
      let attemptedAdd = false;
      let beforeItems = [];
      let failure = '';
      try {
        const beforeResponse = await fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!beforeResponse.ok) throw new Error(this.config.labels.error);
        const before = await beforeResponse.json();
        this.cartItems = before.items || [];
        beforeItems = this.cartItems;
        const selection = this.selection();
        if (selection.error) throw new Error(this.config.labels[selection.error]);
        const drawer = document.querySelector('cart-drawer');
        const nativeDrawer = drawer && typeof drawer.show === 'function' && window.themeVariables;
        const sections = [];
        if (nativeDrawer) document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
        else sections.push('cart-drawer', 'cart-icon-bubble');
        attemptedAdd = true;
        const response = await fetch(`${this.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: selection.items, sections: [...new Set(sections)].slice(0, 5), sections_url: location.pathname })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : this.config.labels.error);
        added = true;
        const cartResponse = await fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!cartResponse.ok) { location.assign(this.config.cart); return; }
        const cart = await cartResponse.json();
        this.cartItems = cart.items || [];
        cart.sections = result.sections;
        if (nativeDrawer) {
          this.form.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: result.items || [result], cart, onSuccessDo: 'force_open_drawer' } }));
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', onSuccessDo: 'force_open_drawer', cart } }));
        } else if (drawer && typeof drawer.renderContents === 'function' && result.sections?.['cart-drawer']) {
          drawer.renderContents(result);
          if (typeof drawer.open === 'function') drawer.open();
        } else location.assign(this.config.cart);
        this.querySelector('[data-cfo-error]').hidden = true;
        const status = this.querySelector('[data-cfo-status]');
        status.textContent = this.config.labels.added;
        status.hidden = false;
      } catch (error) {
        if (added) location.assign(this.config.cart);
        else {
          failure = error.message || this.config.labels.error;
          if (attemptedAdd) {
            try {
              const currentResponse = await fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
              if (!currentResponse.ok) throw new Error();
              const current = await currentResponse.json();
              this.cartItems = current.items || [];
              const quantityOf = (items, id) => items.reduce((sum, item) => sum + (String(item.variant_id ?? item.id) === String(id) ? Number(item.quantity) || 0 : 0), 0);
              this.uncertain = initial.items.some(item => quantityOf(this.cartItems, item.id) > quantityOf(beforeItems, item.id));
              if (this.uncertain) document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            } catch { this.uncertain = true; }
            if (this.uncertain) {
              failure = this.config.labels.review_cart;
              this.querySelector('[data-cfo-cart-link]').hidden = false;
            }
          }
        }
      } finally {
        this.busy = false;
        CrewfordOrder.cartBusy = false;
        this.form.removeAttribute('aria-busy');
        this.paint();
        if (failure) this.showError(failure);
      }
    }
  }
  customElements.define('crewford-order', CrewfordOrder);
})();
